import { IonApp, IonButton, IonContent, IonIcon, IonPage } from "@ionic/react";
import React from "react";
import { refreshOutline, warningOutline } from "ionicons/icons";
import { history } from "@/lib/router/history";
import { logger } from "@/logger";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * How many times a route change may clear the error screen before we stop trying.
 *
 * A throw that survives navigation would otherwise ping-pong between the error screen and the
 * broken tree forever. After this many attempts the screen stays up and the reload button is the
 * way out.
 */
const MAX_AUTOMATIC_RECOVERIES = 3;

/**
 * Global Error Boundary
 *
 * Catches rendering errors and provides a recovery UI instead of showing a white screen. Logs
 * errors for debugging and allows users to attempt recovery by reloading.
 *
 * Mounted twice on purpose: once at the root in `main.tsx`, above the providers, and once inside
 * `App` around the router. The inner one keeps a page-level throw from taking the providers with
 * it; the outer one is the only thing standing between a throw in a provider — or in `App`'s own
 * hooks — and React 18 unmounting the whole document.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private unlistenHistory?: () => void;
  private recoveryAttempts = 0;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidMount() {
    // Navigating away is the user telling us they want to be somewhere else, and the error screen
    // has no way of knowing whether the thing that threw is still on screen. Without this, one bad
    // page turns into an error screen that outlives it — a deeplink, a push notification or a tab
    // tap all land behind it and look exactly like a freeze.
    this.unlistenHistory = history.listen(() => {
      if (this.state.hasError && this.recoveryAttempts < MAX_AUTOMATIC_RECOVERIES) {
        this.recoveryAttempts += 1;
        this.handleReset();
      }
    });
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging
    logger.error(
      {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      },
      "[ErrorBoundary] Rendering error caught",
    );

    this.setState({
      error,
      errorInfo,
    });
  }

  componentWillUnmount() {
    this.unlistenHistory?.();
  }

  handleReload = () => {
    // Reset error state and attempt recovery
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Force reload the page as a last resort
    window.location.reload();
  };

  handleReset = () => {
    // Just reset the error state to try re-rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        // The fallback replaces whatever this boundary wrapped, which at the root means it replaces
        // the app shell too — so it brings its own `IonApp`. Without it the `ion-page` below has no
        // Ionic host to size against, and the recovery screen is itself a white screen.
        <IonApp>
          <IonPage>
            <IonContent className="ion-padding">
              <div className="flex h-full w-full flex-col items-center justify-center gap-6 px-6">
                {/* Icon */}
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-error-10">
                  <IonIcon icon={warningOutline} className="text-5xl text-error-120" />
                </div>

                {/* Title */}
                <h1 className="text-center text-2xl font-bold text-txt-primary">
                  Er is iets misgegaan
                </h1>

                {/* Description */}
                <p className="text-center text-base text-txt-secondary">
                  De app heeft een onverwachte fout ondervonden. Probeer de app opnieuw te laden.
                </p>

                {/* Error details (only in development) */}
                {process.env.NODE_ENV === "development" && this.state.error && (
                  <div className="w-full max-w-md overflow-auto rounded-lg bg-surface-secondary p-4">
                    <p className="font-mono mb-2 text-xs font-semibold text-txt-primary">
                      Error Details:
                    </p>
                    <p className="font-mono text-xs text-txt-secondary">
                      {this.state.error.message}
                    </p>
                    {this.state.error.stack && (
                      <pre className="mt-2 overflow-auto text-2xs text-txt-tertiary">
                        {this.state.error.stack}
                      </pre>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex w-full max-w-md flex-col gap-3">
                  <IonButton expand="block" onClick={this.handleReset}>
                    Probeer opnieuw
                  </IonButton>
                  <IonButton expand="block" fill="outline" onClick={this.handleReload}>
                    <IonIcon slot="start" icon={refreshOutline} />
                    Herlaad app
                  </IonButton>
                </div>
              </div>
            </IonContent>
          </IonPage>
        </IonApp>
      );
    }

    return this.props.children;
  }
}
